import { useCallback, useEffect, useState } from 'react'
import * as Sentry from '@sentry/nextjs'
import { useTranslation } from 'next-i18next'

import { fetchCountries } from 'features/country/services/countriesApi'
import type { Country } from 'features/country/types/country'

type UseCountriesParams = {
  enabled?: boolean
}

export const useCountries = ({ enabled = true }: UseCountriesParams = {}) => {
  const { i18n } = useTranslation()
  const [countries, setCountries] = useState<Country[] | null>(null)
  const [isLoading, setLoading] = useState(false)
  const [isError, setIsError] = useState(false)

  const loadCountries = useCallback(async () => {
    setLoading(true)
    setIsError(false)

    try {
      const { data } = await fetchCountries(i18n.language)
      setCountries(data.countries)
    } catch (error) {
      setIsError(true)
      Sentry.captureException(error instanceof Error ? error : new Error(String(error)), {
        tags: { hook: 'useCountries' },
        extra: { locale: i18n.language },
      })
    } finally {
      setLoading(false)
    }
  }, [i18n.language])

  useEffect(() => {
    if (!enabled) return

    loadCountries()
  }, [enabled, loadCountries])

  return {
    countries,
    isLoading,
    isError,
    refetch: loadCountries,
  }
}
